import React, { useEffect, useState } from 'react'
import { Badge, Button, Col, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { groupService } from '../../services/group.service'
import { organizationService } from '../../services/organization.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { UserCreateData, userService } from '../../services/user.service'
import { Group, Organization, User } from '../../types'
import { parseBackendErrors } from '../../utils/errorHandler'
import { InputSwitch } from '../common/InputSwitch'
import { InputText } from '../common/InputText'
import PermissionsSelect from '../common/PermissionsSelect'
import './UserEditModal.scss'

interface UserEditModalProps {
  show: boolean
  onHide: () => void
  user?: User | null
  onSuccess: (user: User, meta?: { passwordChanged?: boolean }) => void
  onError: (error: any) => void
}

interface UserFormData {
  username: string
  email: string
  password: string
  password_confirm: string
  first_name: string
  last_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  groups: number[]
  organizations: number[]
  default_organization_id: number | null
  organization_roles: Record<string, number[]>
  user_permissions: string[]
}

// Minimal sidebar/tab visibility for new users: Dashboard (always shown),
// Documents, Saved views, plus the standard UI-settings CRUD.
const defaultUserPermissions = [
  permissionsService.getPermissionCode(PermissionAction.Add, PermissionType.UISettings),
  permissionsService.getPermissionCode(PermissionAction.View, PermissionType.UISettings),
  permissionsService.getPermissionCode(PermissionAction.Change, PermissionType.UISettings),
  permissionsService.getPermissionCode(PermissionAction.Delete, PermissionType.UISettings),
  permissionsService.getPermissionCode(PermissionAction.Display, PermissionType.Document),
  permissionsService.getPermissionCode(PermissionAction.Display, PermissionType.SavedView),
  permissionsService.getPermissionCode(PermissionAction.Display, PermissionType.UISettings),
]

const UserEditModal: React.FC<UserEditModalProps> = ({ show, onHide, user, onSuccess, onError }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    is_active: true,
    is_staff: true,
    is_superuser: false,
    groups: [],
    organizations: [],
    default_organization_id: null,
    organization_roles: {},
    user_permissions: defaultUserPermissions,
  })
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [groupsByOrganization, setGroupsByOrganization] = useState<Record<number, Group[]>>({})
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [, setPasswordIsSet] = useState(false)
  const [totpLoading, setTotpLoading] = useState(false)
  const [revealPassword, setRevealPassword] = useState(false)
  const [revealPasswordConfirm, setRevealPasswordConfirm] = useState(false)

  const isEdit = !!user
  const currentUserIsSuperUser = permissionsService.isSuperUser()
  const canChangeUser = permissionsService.currentUserCan(PermissionAction.Change, PermissionType.User)
  const isLdapUser = isEdit && user?.auth_source === 'ldap'

  useEffect(() => {
    if (show) {
      loadOrganizations()
      if (user) {
        setFormData({
          username: user.username || '',
          email: user.email || '',
          password: '',
          password_confirm: '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          is_active: user.is_active ?? true,
          is_staff: user.is_staff ?? true,
          is_superuser: user.is_superuser ?? false,
          groups: user.groups || [],
          organizations: user.organizations || [],
          default_organization_id: user.default_organization_id ?? user.organizations?.[0] ?? null,
          organization_roles: user.organization_roles || {},
          user_permissions: user.user_permissions || [],
        })
        setPasswordIsSet(!!user.password)
      } else {
        setFormData({
          username: '',
          email: '',
          password: '',
          password_confirm: '',
          first_name: '',
          last_name: '',
          is_active: true,
          is_staff: true,
          is_superuser: false,
          groups: [],
          organizations: [],
          default_organization_id: null,
          organization_roles: {},
          user_permissions: defaultUserPermissions,
        })
        setPasswordIsSet(false)
      }
      setErrors({})
    }
  }, [show, user])

  const currentOrganizationId = settingsService.getCurrentOrganizationId()

  const loadOrganizations = async () => {
    try {
      const response = await organizationService.listAll({ page_size: 100000 })
      const orgs = response.results
      setOrganizations(orgs)

      if (currentOrganizationId) {
        const groupsResponse = await groupService.listAll({ page_size: 100000, organization_id: currentOrganizationId })
        setGroupsByOrganization({ [currentOrganizationId]: groupsResponse.results })
      }
    } catch (error) {}
  }

  const handleInputChange = (field: keyof UserFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handleToggleSuperUser = () => {
    const newIsSuperUser = !formData.is_superuser
    setFormData((prev) => ({
      ...prev,
      is_superuser: newIsSuperUser,
      user_permissions: newIsSuperUser ? [] : prev.user_permissions,
    }))
  }

  const getInheritedPermissions = (): string[] => {
    const selectedGroupIds = Object.values(formData.organization_roles || {}).flat()
    if (selectedGroupIds.length === 0) return []

    const allGroups = Object.values(groupsByOrganization).flat()
    return selectedGroupIds.flatMap((groupId) => {
      const group = allGroups.find((g) => g.id === groupId)
      return group?.permissions || []
    })
  }

  const handleSave = async () => {
    setLoading(true)
    setErrors({})

    try {
      const shouldValidatePassword = !isEdit || formData.password || formData.password_confirm
      if (shouldValidatePassword) {
        const validationErrors: Record<string, string> = {}

        if (!formData.password) {
          validationErrors.password = t('error_field_required')
        }
        if (!formData.password_confirm) {
          validationErrors.password_confirm = t('error_field_required')
        } else if (formData.password !== formData.password_confirm) {
          validationErrors.password_confirm = t('passwords_do_not_match')
        }

        if (Object.keys(validationErrors).length > 0) {
          setErrors((prev) => ({
            ...prev,
            ...validationErrors,
          }))
          setLoading(false)
          return
        }
      }

      const userData: UserCreateData = {
        username: formData.username,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        is_active: formData.is_active,
        is_staff: isEdit && user ? user.is_staff : formData.is_staff,
        is_superuser: currentUserIsSuperUser ? formData.is_superuser : isEdit && user ? user.is_superuser : false,
        groups: formData.groups,
        organizations: formData.organizations,
        default_organization_id: formData.default_organization_id,
        organization_roles: formData.organization_roles,
        user_permissions: formData.user_permissions,
      }

      // Only include password if a new one is provided
      if (!isEdit || formData.password) {
        userData.password = formData.password
      }
      const passwordChanged = !!(isEdit && userData.password)

      let savedUser: User
      if (isEdit && user) {
        savedUser = await userService.update(user.id, userData)
      } else {
        if (!formData.password) {
          throw new Error('Password is required for new users')
        }
        userData.password = formData.password
        savedUser = await userService.create(userData)
      }

      toastService.showSuccess(t('saved_user', { username: savedUser.username }))
      onSuccess(savedUser, { passwordChanged })
      onHide()
    } catch (error: any) {
      const { fieldErrors, errorResponse } = parseBackendErrors(error, t, t('error_saving_user', { username: formData.username }))

      setErrors(fieldErrors)
      toastService.showError(t('error_saving_user', { username: formData.username }), errorResponse)

      if (!error.response?.data) {
        onError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDeactivateTotp = async () => {
    if (!user) return

    setTotpLoading(true)
    try {
      const success = await userService.deactivateTotp(user)
      if (success) {
        toastService.showInfo(t('totp_deactivated'))
        // Update user object
        const updatedUser = { ...user, is_mfa_enabled: false }
        onSuccess(updatedUser, { passwordChanged: false })
      } else {
        toastService.showError(t('totp_deactivation_failed'))
      }
    } catch (error) {
      toastService.showError(t('totp_deactivation_failed'))
    } finally {
      setTotpLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? t('edit_user_account') : t('create_new_user_account')}
            {user?.id && (
              <Badge bg="primary" className="ms-2">
                ID: {user.id}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row>
            <Col md={4}>
              <InputText
                title={t('username')}
                value={formData.username}
                onChange={(value) => handleInputChange('username', value)}
                error={errors.username}
                disabled={isEdit && !canChangeUser}
              />

              <InputText
                title={t('email')}
                type="email"
                value={formData.email}
                onChange={(value) => handleInputChange('email', value)}
                error={errors.email}
                disabled={isEdit && !canChangeUser}
              />

              {isLdapUser ? (
                <div className="mb-3">
                  <div className="alert alert-info py-2 mb-0 small d-flex align-items-center gap-2">
                    <i className="bi bi-diagram-3"></i>
                    <span>{t('ad.password_managed_by_ad')}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      {t('password')}
                    </label>
                    <div className="input-group">
                      <input
                        type={revealPassword ? 'text' : 'password'}
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        id="password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder={isEdit ? t('enter_new_password') : t('password')}
                        disabled={isEdit && !canChangeUser}
                        autoComplete="new-password"
                      />
                      <button className="btn btn-outline-secondary" type="button" onClick={() => setRevealPassword(!revealPassword)}>
                        <i className={revealPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                      </button>
                    </div>
                    {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="password_confirm" className="form-label">
                      {t('confirm_password')}
                    </label>
                    <div className="input-group">
                      <input
                        type={revealPasswordConfirm ? 'text' : 'password'}
                        className={`form-control ${errors.password_confirm ? 'is-invalid' : ''}`}
                        id="password_confirm"
                        value={formData.password_confirm}
                        onChange={(e) => handleInputChange('password_confirm', e.target.value)}
                        placeholder={isEdit ? t('confirm_new_password') : t('confirm_password')}
                        disabled={isEdit && !canChangeUser}
                        autoComplete="new-password"
                      />
                      <button className="btn btn-outline-secondary" type="button" onClick={() => setRevealPasswordConfirm(!revealPasswordConfirm)}>
                        <i className={revealPasswordConfirm ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                      </button>
                    </div>
                    {errors.password_confirm && <div className="invalid-feedback d-block">{errors.password_confirm}</div>}
                  </div>
                </>
              )}

              <InputText
                title={t('first_name')}
                value={formData.first_name}
                onChange={(value) => handleInputChange('first_name', value)}
                error={errors.first_name}
                disabled={isEdit && !canChangeUser}
              />

              <InputText
                title={t('last_name')}
                value={formData.last_name}
                onChange={(value) => handleInputChange('last_name', value)}
                error={errors.last_name}
                disabled={isEdit && !canChangeUser}
              />

              <InputSwitch
                title={t('active')}
                value={formData.is_active}
                onChange={(value) => handleInputChange('is_active', value)}
                horizontal={true}
                disabled={isEdit && !canChangeUser}
              />
              {/*
                Administrator (is_staff): intentionally not shown; value on save uses server for edits (see userData.is_staff).
              <InputSwitch
                title={t('admin')}
                value={formData.is_staff}
                onChange={(value) => handleInputChange('is_staff', value)}
                horizontal={true}
                hint={t('access_logs')}
                disabled={isEdit && !canChangeUser}
              />
              */}
              {currentUserIsSuperUser && (
                <InputSwitch
                  title={t('superuser')}
                  value={formData.is_superuser}
                  onChange={handleToggleSuperUser}
                  horizontal={true}
                  hint={t('grants_all_permissions_and_can_view_all')}
                  disabled={isEdit && !canChangeUser}
                />
              )}

              <div className="mb-3">
                <label className="form-label mb-2">{t('organizations')}</label>
                <div className="docdot-input-select">
                  <Select
                    isMulti
                    options={organizations.map((organization) => ({
                      value: organization.id,
                      label: organization.name,
                    }))}
                    value={organizations
                      .filter((organization) => formData.organizations.includes(organization.id))
                      .map((organization) => ({
                        value: organization.id,
                        label: organization.name,
                      }))}
                    onChange={(selectedOptions) => {
                      const selectedOrganizations = selectedOptions ? selectedOptions.map((option) => option.value) : []
                      const nextRoles: Record<string, number[]> = {}
                      Object.entries(formData.organization_roles || {}).forEach(([orgId, roleIds]) => {
                        if (selectedOrganizations.includes(Number(orgId))) {
                          nextRoles[orgId] = roleIds
                        }
                      })
                      setFormData((prev) => ({
                        ...prev,
                        organizations: selectedOrganizations,
                        default_organization_id: selectedOrganizations.includes(prev.default_organization_id || -1)
                          ? prev.default_organization_id
                          : (selectedOrganizations[0] ?? null),
                        organization_roles: nextRoles,
                      }))
                    }}
                    placeholder={t('select_organizations')}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable={false}
                    closeMenuOnSelect={false}
                    isDisabled={isEdit && !canChangeUser}
                  />
                </div>
                {errors.organizations && <div className="text-danger small mt-1">{errors.organizations}</div>}
              </div>

              {formData.organizations.length > 1 && (
                <div className="mb-3">
                  <label className="form-label mb-2">{t('default_organization')}</label>
                  <div className="docdot-input-select">
                    <Select
                      options={organizations
                        .filter((organization) => formData.organizations.includes(organization.id))
                        .map((organization) => ({
                          value: organization.id,
                          label: organization.name,
                        }))}
                      value={
                        organizations
                          .filter((organization) => organization.id === formData.default_organization_id)
                          .map((organization) => ({
                            value: organization.id,
                            label: organization.name,
                          }))[0] || null
                      }
                      onChange={(selectedOption) => {
                        handleInputChange('default_organization_id', selectedOption?.value ?? null)
                      }}
                      placeholder={t('organization')}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable={true}
                      isDisabled={isEdit && !canChangeUser}
                    />
                  </div>
                  {errors.default_organization_id && <div className="text-danger small mt-1">{errors.default_organization_id}</div>}
                </div>
              )}

              {currentOrganizationId && formData.organizations.includes(currentOrganizationId) && (() => {
                const currentOrg = organizations.find((o) => o.id === currentOrganizationId)
                const orgGroups = groupsByOrganization[currentOrganizationId] || []
                const selectedRoles = formData.organization_roles?.[String(currentOrganizationId)] || []
                return (
                  <div className="mb-3">
                    <label className="form-label mb-2">{t('roles_for')} {currentOrg?.name || ''}</label>
                    <div className="docdot-input-select">
                      <Select
                        isMulti
                        options={orgGroups.map((group) => ({
                          value: group.id,
                          label: group.name,
                        }))}
                        value={orgGroups
                          .filter((group) => selectedRoles.includes(group.id))
                          .map((group) => ({ value: group.id, label: group.name }))}
                        onChange={(selectedOptions) => {
                          const selectedGroups = selectedOptions ? selectedOptions.map((option) => option.value) : []
                          setFormData((prev) => ({
                            ...prev,
                            groups: selectedGroups,
                            organization_roles: {
                              ...(prev.organization_roles || {}),
                              [String(currentOrganizationId)]: selectedGroups,
                            },
                          }))
                        }}
                        placeholder={t('select_groups')}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isClearable={false}
                        closeMenuOnSelect={false}
                        isDisabled={isEdit && !canChangeUser}
                      />
                    </div>
                  </div>
                )
              })()}

              {user?.is_mfa_enabled && currentUserIsSuperUser && (
                <div className="mb-3">
                  <label className="form-label">{t('two_factor_authentication')}</label>
                  <Button variant="outline-danger" size="sm" onClick={handleDeactivateTotp} disabled={totpLoading}>
                    <i className="bi bi-trash me-1"></i>
                    {t('disable_two_factor_authentication')}
                  </Button>
                </div>
              )}
            </Col>

            <Col>
              <PermissionsSelect
                title={t('permissions')}
                value={formData.user_permissions}
                onChange={(permissions: string[]) => handleInputChange('user_permissions', permissions)}
                error={errors.user_permissions}
                inheritedPermissions={getInheritedPermissions()}
                disabled={formData.is_superuser || (isEdit && !canChangeUser)}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={loading || (isEdit && !canChangeUser)}>
            {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
            {t('save')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default UserEditModal
